import { useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { useCommonCodeValidate } from "src/utils/commonCode/CommonCodeValidateContext";
import {
	buildCommonCodeRequirements,
	MST4040_R01_ROW_VALIDATE,
} from "src/utils/commonCode/commonCodeRequirementConfig";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { showError, showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import {
	createMST4040_R01,
	deleteMST4040_R01,
	downloadExcel_MST4040_R01,
	getMST4040_R01AllCount,
	getMST4040_R01List,
	getMST4040_R01One,
	patchMST4040_R01,
} from "./Api_MST4040_R01";
import { defaultMST4040_R01Dto, MST4040_R01Dto } from "./Dto_MST4040_R01";
import { MST4040_R01Filter, useMST4040_R01Store } from "./Store_MST4040_R01";

interface Props {
	gridRef: React.RefObject<AgGridReact<MST4040_R01Dto> | null>;
	setMessage: (message: string) => void;
}

export const Content_MST4040_R01 = (props: Props) => {
	const { gridRef, setMessage } = props;

	useFetchActionbarAuth("MST4040_R01");

	const {
		selectedMST4040_R01,
		setSelectedMST4040_R01,
		MST4040_R01List,
		setMST4040_R01List,
		editMST4040_R01,
		setEditMST4040_R01,
		initSelectedMST4040_R01,
		initData,
		MST4040_R01Filter,
		setMST4040_R01PastFilter,
		setAllCount,
		setScrollTop,
	} = useMST4040_R01Store();

	const { moldClassList, moldTypeList, eqmStatusList, yesNoList } = useCommonCodeStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();

	const getParams = (filter: Partial<MST4040_R01Filter>) => {
		return {
			moldCd: filter.moldCd,
			moldClass: filter.moldClass,
		};
	};

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const response = await downloadExcel_MST4040_R01(
			'',
			getParams(MST4040_R01Filter),
			MST4040_R01List.length,
		);

		downloadSettingExcel({
			responseData: response.data.data,
			fileName: '금형 기준정보 등록-excel.xlsx',
		});

		setLoadingSpinner(false);
	}, [MST4040_R01Filter, MST4040_R01List, setLoadingSpinner]);

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMST4040_R01AllCount(getParams(MST4040_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [MST4040_R01Filter, setAllCount]);

	const defaultMST4040_R01Row = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(MST4040_R01_ROW_VALIDATE, {
				moldClassList,
				moldTypeList,
				eqmStatusList,
				yesNoList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultMST4040_R01Dto };
		}

		return {
			...defaultMST4040_R01Dto,
			moldClass: moldClassList[0]?.minorCd || '',
			moldType: moldTypeList[0]?.minorCd || '',
			eqmstatus: eqmStatusList[0]?.minorCd || '',
			useYn: yesNoList[0]?.minorCd || '',
			inserted: true,
		};
	}, [moldClassList, moldTypeList, eqmStatusList, yesNoList, validateCommonCodeLists]);

	const handleCreateClick = useCallback(async () => {
		initSelectedMST4040_R01();
		setEditMST4040_R01(defaultMST4040_R01Row());
	}, [initSelectedMST4040_R01, setEditMST4040_R01, defaultMST4040_R01Row]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);

		const responseData = await getMST4040_R01List(
			'',
			getParams(MST4040_R01Filter),
			MST4040_R01List.length,
		);

		const newList = [...responseData.data.data];
		setMST4040_R01List(newList);

		const processAllCount = await renewalAllCount();

		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				setSelectedMST4040_R01,
				() => true,
			);
			setMST4040_R01PastFilter(MST4040_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		} else {
			showWarning('조회된 데이터가 없습니다.');
			handleCreateClick();
			setSelectedMST4040_R01(undefined);
		}
		setLoadingSpinner(false);
		return newList;
	}, [
		MST4040_R01Filter,
		MST4040_R01List,
		gridRef,
		setMST4040_R01List,
		setSelectedMST4040_R01,
		setScrollTop,
		renewalAllCount,
		handleCreateClick,
		setMessage,
		setLoadingSpinner,
		setMST4040_R01PastFilter,
	]);

	const fetchOne = useCallback(async () => {
		if (!selectedMST4040_R01) return;
		setLoadingSpinner(true);
		try {
			const response = await getMST4040_R01One(selectedMST4040_R01.moldCd);
			setEditMST4040_R01({ ...defaultMST4040_R01Row(), ...response.data.data });
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}
		setLoadingSpinner(false);
	}, [selectedMST4040_R01, setEditMST4040_R01, defaultMST4040_R01Row, setLoadingSpinner]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleDeleteClick = useCallback(async () => {
		if (!selectedMST4040_R01) {
			showWarning('선택된 금형이 없습니다.');
			return;
		}

		if (!window.confirm('정말로 삭제하시겠습니까?')) {
			return;
		}

		await deleteMST4040_R01(selectedMST4040_R01.moldCd);
		refetchUpdateList(
			'delete',
			defaultMST4040_R01Row(),
			selectedMST4040_R01,
			'moldCd',
			MST4040_R01List,
			setMST4040_R01List,
		);
		handleCreateClick();
		showSuccess(`${selectedMST4040_R01.moldCd} 금형 삭제가 완료되었습니다.`);
	}, [selectedMST4040_R01, defaultMST4040_R01Row, MST4040_R01List, setMST4040_R01List, handleCreateClick]);

	const validateInsertData = (data: MST4040_R01Dto) => {
		if (!validateString(data.moldCd)) {
			showWarning('금형코드를 입력해주세요.');
			return false;
		}
		if (!validateString(data.moldNm)) {
			showWarning('금형명을 입력해주세요.\n금형코드 : ' + editMST4040_R01.moldCd);
			return false;
		}
		if (!validateString(data.moldType)) {
			showWarning('금형유형을 선택해주세요.\n금형코드 : ' + editMST4040_R01.moldCd);
			return false;
		}
		if (!validateString(data.eqmstatus)) {
			showWarning('상태를 선택해주세요.\n금형코드 : ' + editMST4040_R01.moldCd);
			return false;
		}
		if (!validateString(data.useYn)) {
			showWarning('사용여부를 선택해주세요.\n금형코드 : ' + editMST4040_R01.moldCd);
			return false;
		}

		return true;
	};

	const handleSaveClick = useCallback(async () => {
		if (!validateInsertData(editMST4040_R01)) {
			return;
		}

		let processName = '';
		let responseData: MST4040_R01Dto = { ...defaultMST4040_R01Row() };

		if (selectedMST4040_R01) {
			processName = 'patch';
			const response = await patchMST4040_R01(editMST4040_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editMST4040_R01.moldCd} 금형 수정이 완료되었습니다.`);
					setMessage(`${editMST4040_R01.moldCd} 금형 수정이 완료되었습니다.`);
				} else return;
			}
		} else {
			processName = 'post';
			const response = await createMST4040_R01(editMST4040_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editMST4040_R01.moldCd} 금형 등록이 완료되었습니다.`);
					setMessage(`${editMST4040_R01.moldCd} 금형 등록이 완료되었습니다.`);
				} else return;
			}
		}

		setTimeout(() => {
			setMessage("");
		}, 5000);

		if (editMST4040_R01) {
			refetchUpdateList(
				processName,
				responseData,
				editMST4040_R01,
				'moldCd',
				MST4040_R01List,
				setMST4040_R01List,
			);
			setSelectedMST4040_R01(responseData);
		}
	}, [
		selectedMST4040_R01,
		editMST4040_R01,
		defaultMST4040_R01Row,
		MST4040_R01List,
		setMST4040_R01List,
		setSelectedMST4040_R01,
		setMessage,
	]);

	useEffect(() => {
		if (selectedMST4040_R01) {
			fetchOne();
		} else {
			setEditMST4040_R01(defaultMST4040_R01Row());
		}
	}, [selectedMST4040_R01]);

	return {
		handleExcelClick,
		fetchData,
		defaultMST4040_R01Row,
		initContainerData,
		handleCreateClick,
		handleDeleteClick,
		handleSaveClick,
	};
};
